/**
 * Contexto de fotos primary del catálogo. Carga 1 vez tras login y mantiene un
 * Map { sticker_id → public_url } para que BookSlot decida qué imagen mostrar
 * sin hacer fetch por cada slot.
 *
 * Refresh manual cuando se sube/elimina/promueve una foto admin.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchPrimaryPhotos } from "../api/stickerPhotos";
import { useAuth } from "./AuthContext";

interface PhotosValue {
  primaries: Record<number, string>;
  refresh: () => Promise<void>;
}

const PhotosContext = createContext<PhotosValue | null>(null);

export function PhotosProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [primaries, setPrimaries] = useState<Record<number, string>>({});

  const refresh = useCallback(async () => {
    try {
      const data = await fetchPrimaryPhotos();
      setPrimaries(data || {});
    } catch {
      /* silent: si falla, BookSlot cae a image_url del catálogo */
    }
  }, []);

  useEffect(() => {
    if (status === "authenticated") refresh();
    else setPrimaries({});
  }, [status, refresh]);

  const value = useMemo<PhotosValue>(() => ({ primaries, refresh }), [primaries, refresh]);
  return <PhotosContext.Provider value={value}>{children}</PhotosContext.Provider>;
}

export function usePhotos(): PhotosValue {
  const ctx = useContext(PhotosContext);
  if (!ctx) throw new Error("usePhotos() debe estar dentro de <PhotosProvider>");
  return ctx;
}
